import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, BarChart3, Sparkles } from "lucide-react";

function StatCard({ label, value, caption, Icon, valueClass, iconColor }) {
  return (
    <Card className="glass-effect">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400 uppercase tracking-wider">{label}</span>
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        <div className={`text-2xl font-bold mono mb-1 ${valueClass}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          {value}
        </div>
        <div className="text-xs text-gray-500">{caption}</div>
      </CardContent>
    </Card>
  );
}

function formatAgo(seconds) {
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
}

/** Pulsing "LIVE · 98/100 · updated Xs ago" pill computed from per-asset source fields. */
function LivePill({ assets }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(id);
  }, []);

  const liveCount = assets.filter((a) => a.source === "live").length;
  if (!liveCount) return null;
  const newest = assets.reduce((max, a) => {
    const ts = a.last_updated ? Date.parse(a.last_updated) : 0;
    return ts > max ? ts : max;
  }, 0);
  const secondsAgo = newest ? Math.max(0, Math.round((Date.now() - newest) / 1000)) : null;

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-[#00FFA3]"
      data-testid="live-data-pill"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FFA3] opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FFA3]" />
      </span>
      LIVE · {liveCount}/{assets.length}
      {secondsAgo !== null && <span className="text-gray-500 font-normal">· updated {formatAgo(secondsAgo)}</span>}
    </span>
  );
}

export default function MarketStatsStrip({ marketData }) {
  const assets = [...(marketData.stocks || []), ...(marketData.cryptos || [])];
  const total = assets.length;
  const hasLive = assets.some((a) => a.source === "live");
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
    >
      <StatCard
        label="Market Trend"
        value="Bullish"
        caption="Based on AI analysis"
        Icon={TrendingUp}
        iconColor="#00FFA3"
        valueClass="text-[#00FFA3]"
      />
      <StatCard
        label="Total Assets"
        value={total}
        caption={hasLive ? <LivePill assets={assets} /> : "Stocks & Crypto"}
        Icon={BarChart3}
        iconColor="#00E0FF"
        valueClass="text-white"
      />
      <StatCard
        label="AI Confidence"
        value="94%"
        caption="Prediction accuracy"
        Icon={Sparkles}
        iconColor="#6366F1"
        valueClass="text-white"
      />
    </motion.div>
  );
}
